import { z } from 'zod';
import {
    PortfolioAmountPart,
    PortfolioEnterpriseProductPriceAdjustment,
    PortfolioProductCharging,
    PortfolioProductConsumptionMoment,
    PortfolioProductOptions,
    PortfolioPromotions,
} from '@mews-commander/data-access';
import {
    basePriceSchema,
    servicesConfigurationSchema,
    ServicesFieldNames,
    taxesSchema,
} from '../../forms/schemas/configureProductFormSchema';
import { RepricingFieldName } from './repricingOptions';

export const availabilitySchema = z.object({
    [ServicesFieldNames.Options]: z.array(z.nativeEnum(PortfolioProductOptions)),
    [ServicesFieldNames.Promotions]: z.array(z.nativeEnum(PortfolioPromotions)),
});

export const repricingSchema = z.object({
    [RepricingFieldName.RepriceExistingReservations]: z.boolean(),
});

export enum PriceAdjustmentFieldNames {
    PriceAdjustment = 'priceAdjustment',
    Discriminator = 'discriminator',
    FixedValue = 'fixedValue',
    PercentageValue = 'percentageValue',
}

export enum AddProductToServiceFieldNames {
    ServiceProducts = 'serviceProducts',
}

export enum PriceAdjustmentDiscriminator {
    None = 'None',
    Fixed = PortfolioEnterpriseProductPriceAdjustment.Fixed,
    Percentage = PortfolioEnterpriseProductPriceAdjustment.Percentage,
}

export const fixedPriceAdjustmentUpsertSchema = z.object({
    value: z.number().nonnegative(),
});

export const percentagePriceAdjustmentUpsertSchema = z.object({
    amountPart: z.nativeEnum(PortfolioAmountPart),
    multiplier: z.number(),
});

export const chargingSchema = z.object({
    [ServicesFieldNames.Charging]: z.nativeEnum(PortfolioProductCharging),
    [ServicesFieldNames.ConsumptionMoment]: z.nativeEnum(PortfolioProductConsumptionMoment).nullable(),
});

export const servicePriceAdjustmentSchema = z.object({
    [PriceAdjustmentFieldNames.PriceAdjustment]: z.discriminatedUnion('discriminator', [
        z.object({
            [PriceAdjustmentFieldNames.Discriminator]: z.literal(PriceAdjustmentDiscriminator.Fixed),
            [PriceAdjustmentFieldNames.FixedValue]: fixedPriceAdjustmentUpsertSchema,
        }),
        z.object({
            [PriceAdjustmentFieldNames.Discriminator]: z.literal(PriceAdjustmentDiscriminator.Percentage),
            [PriceAdjustmentFieldNames.PercentageValue]: percentagePriceAdjustmentUpsertSchema,
        }),
        z.object({
            [PriceAdjustmentFieldNames.Discriminator]: z.literal(PriceAdjustmentDiscriminator.None),
        }),
    ]),
});

export const servicesProductSchema = availabilitySchema
    .merge(chargingSchema)
    .merge(servicePriceAdjustmentSchema)
    .merge(repricingSchema);

export const addProductToServiceSchema = servicesConfigurationSchema.extend({
    [AddProductToServiceFieldNames.ServiceProducts]: z.array(
        chargingSchema.merge(availabilitySchema).merge(servicePriceAdjustmentSchema)
    ),
});

export const pricingConfigurationSchema = basePriceSchema.merge(taxesSchema).merge(repricingSchema);
